import React, { useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { LoaderV2 } from "components/common/LoaderV2/LoaderV2";
import { LoaderMode, LoaderTheme } from "components/common/LoaderV2/LoaderV2";
import { FaCog } from "react-icons/fa";
import { SETTINGS_SECTIONS } from "../Settings.sections";

interface SettingsFallbackProps {
  label?: string;
}

export const SettingsFallback: React.FC<SettingsFallbackProps> = ({
  label = "Loading settings…",
}) => {
  const location = useLocation();

  const { icon, theme, texts } = useMemo(() => {
    // Match current path against settings registry using router's matcher
    const pathname = location.pathname;
    const matched = [...SETTINGS_SECTIONS]
      .sort((a, b) => b.path.length - a.path.length)
      .find((section) => !!matchPath({ path: section.path, end: false }, pathname));

    const accentToTheme: Record<string, LoaderTheme> = {
      indigo: LoaderTheme.indigo,
      emerald: LoaderTheme.green,
      blue: LoaderTheme.blue,
      teal: LoaderTheme.purple, // closest in LoaderTheme palette
      cyan: LoaderTheme.blue,
      violet: LoaderTheme.violet,
    };

    const defaultTexts = [
      label,
      "Fetching preferences…",
      "Preparing UI…",
      "Loading components…",
      "Almost there…",
    ];

    const textsById: Record<string, string[]> = {
      "profile": [
        "Loading profile…",
        "Fetching user info…",
        "Syncing fields…",
        "Applying preferences…",
        "Almost there…",
      ],
      "security": [
        "Loading security…",
        "Validating session…",
        "Preparing MFA…",
        "Securing forms…",
        "Almost there…",
      ],
      "account": [
        "Loading account…",
        "Fetching preferences…",
        "Preparing UI…",
        "Saving your spot…",
        "Almost there…",
      ],
      "billing": [
        "Loading billing…",
        "Retrieving subscription…",
        "Getting payment methods…",
        "Checking status…",
        "Almost there…",
      ],
      "referrals": ["Loading referrals…", "Fetching your link…", "Loading stats…", "Almost there…"],
      "team": [
        "Loading team…",
        "Fetching members…",
        "Checking permissions…",
        "Prepping invites…",
        "Almost there…",
      ],
      "notifications-list": [
        "Loading notifications…",
        "Fetching latest…",
        "Updating counts…",
        "Applying filters…",
        "Almost there…",
      ],
    };

    if (!matched) {
      return {
        icon: <FaCog className="text-2xl text-hue-indigo" />,
        theme: LoaderTheme.indigo,
        texts: defaultTexts,
      };
    }

    return {
      icon: <span className="text-foreground/80">{matched.icon}</span>,
      theme: accentToTheme[matched.accent] ?? LoaderTheme.indigo,
      texts: textsById[matched.id] ?? defaultTexts,
    };
  }, [label, location.pathname]);

  return (
    <div className="p-8 w-full h-full flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="mb-1">{icon}</div>
        <LoaderV2
          mode={LoaderMode.inline}
          theme={theme}
          size="md"
          rotatingTexts={texts}
          textClassName="text-muted-foreground text-sm"
        />
      </div>
    </div>
  );
};

export default SettingsFallback;
